import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { Box, Typography, useTheme } from '@mui/material';
import type { RecallKind } from '@prospero/shared';
import { useTranslation } from 'react-i18next';
import { diffDaysFromAppToday } from '../../config/timezone';
import {
  getLotStateColors,
  type LotVisualState,
  resolveLotVisualState,
} from '../../pages/lots/utils/lot-visual-state';
import type { LotStatus } from '../../services/lots/lots.types';
import { formatDate } from '../../utils/date-formatter';
import { getBlockedLotLabelSuffix } from '../../utils/lot-status';
import { formatDateKey } from '../calendar/calendar-utils';

export interface LotExpiryChipProps {
  status: LotStatus;
  expiryDate: Date | string | null;
  hasNoExpiry: boolean;
  exhaustedAt: Date | string | null;
  /** Tipo del RecallCase che blocca il lotto. Assente/null => badge generico. */
  blockingRecallKind?: RecallKind | null;
}

function getExpiryLabel(
  state: LotVisualState,
  expiryDate: string | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
  blockingRecallKind: RecallKind | null | undefined,
): string {
  if (state === 'exhausted') {
    return t('lots.statusBadge.exhausted');
  }
  if (state === 'blocked') {
    return t(`lots.${getBlockedLotLabelSuffix(blockingRecallKind)}`);
  }
  if (!expiryDate) {
    return t('lots.expiryUnknown');
  }
  const expiry = new Date(expiryDate);
  if (Number.isNaN(expiry.getTime())) {
    return t('lots.expiryUnknown');
  }
  if (state === 'expired') {
    return t('lots.expiryExpired');
  }
  if (state === 'expiring-today') {
    return t('lots.expiryToday');
  }
  if (state === 'expiring-soon') {
    const diffDays = diffDaysFromAppToday(expiryDate) ?? 0;
    return t('lots.expiryInDays', { count: diffDays, date: formatDate(expiryDate) });
  }
  return formatDate(expiryDate);
}

function normalizeDate(value: Date | string | null): string | null {
  if (value === null) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : formatDateKey(value);
  }
  return value;
}

export function LotExpiryChip({
  status,
  expiryDate,
  hasNoExpiry,
  blockingRecallKind,
}: LotExpiryChipProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const normalizedExpiry = hasNoExpiry ? null : normalizeDate(expiryDate);

  const visualState = resolveLotVisualState({
    status,
    expiryDate: normalizedExpiry,
  });
  const stateColors = getLotStateColors(theme, visualState);

  const label = getExpiryLabel(visualState, normalizedExpiry, t, blockingRecallKind);
  const StateIcon = visualState === 'blocked' ? BlockOutlinedIcon : EventOutlinedIcon;

  return (
    <Box data-testid="lot-expiry-chip" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <StateIcon sx={{ fontSize: 14, color: stateColors.dark }} aria-hidden="true" />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          fontSize: '0.74rem',
          color: stateColors.dark,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
